import React, { useState } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import { PDFViewer } from "@react-pdf/renderer";
import { CoursePlanPDF } from "@/components/pdf/CoursePlanPDF";

// Mock data for the Course Plan PDF
const mockCoursePlanData = {
  climber: {
    firstName: "Matthew",
    lastName: "Randall",
    preferredID: "123456789",
  },
  schoolName: "Bush Middle School",
  program: {
    programName: "Reagan High School",
    matchType: "HOME_CAMPUS_MAGNET", // Shows both home and magnet icons in the legend
  },
  cteCourseSequence: {
    name: "Business Management Option 1",
    tag: undefined,
    coursesByGrade: [
      {
        grade: 8,
        courses: [
          {
            name: "Principles of Business, Marketing & Finance",
            code: "M0802",
          },
        ],
      },
      {
        grade: 9,
        courses: [
          {
            name: "Foundations of Business Communications and Technologies",
            code: "H8291",
          },
        ],
      },
      {
        grade: 10,
        courses: [
          {
            name: "Business Communications and Technologies",
            code: "H8292",
          },
          {
            name: "Advanced Business Communications",
            code: "H8293",
          },
          {
            name: "Business Technology Applications",
            code: "H8294",
          },
        ],
      },
      {
        grade: 11,
        courses: [
          {
            name: "Business Management",
            code: "H8294",
          },
        ],
      },
      {
        grade: 12,
        courses: [
          {
            name: "Career Preparation",
            code: "H6796",
          },
        ],
      },
    ],
  },
  pathway: {
    id: "pathway-1",
    name: "Business Management",
  },
  districtPathways: [
    {
      id: "pathway-1",
      name: "Business Management",
      color: "#FF6B9D", // Pink
    },
    {
      id: "pathway-2",
      name: "STEM Academy",
      color: "#4ECDC4", // Teal
    },
    {
      id: "pathway-3",
      name: "Arts & Humanities",
      color: "#FFE66D", // Yellow
    },
    {
      id: "pathway-4",
      name: "Health Sciences",
      color: "#95E1D3", // Mint
    },
    {
      id: "pathway-5",
      name: "Engineering",
      color: "#F38181", // Coral
    },
  ],
  coreCourseSequences: [
    {
      name: "English Language Arts & Reading",
      tag: "Hyper Accelerated",
      districtPathwayIds: ["pathway-3"],
      coursesByGrade: [
        {
          grade: 8,
          courses: [
            {
              name: "English Language Arts & Reading (ELAR 8)",
              code: "M0802",
            },
          ],
        },
        {
          grade: 9,
          courses: [
            {
              name: "English I",
              code: "H1113",
            },
          ],
        },
        {
          grade: 10,
          courses: [
            {
              name: "English II Honors",
              code: "H1213",
            },
            {
              name: "AP English Language",
              code: "H1214",
            },
            {
              name: "Creative Writing",
              code: "H1215",
            },
          ],
        },
        {
          grade: 11,
          courses: [
            {
              name: "English III",
              code: "H1313",
            },
          ],
        },
        {
          grade: 12,
          courses: [
            {
              name: "English IV",
              code: "H1413",
            },
            {
              name: "AP English Literature",
              code: "H1414",
            },
            {
              name: "Advanced Composition",
              code: "H1415",
            },
          ],
        },
      ],
    },
    {
      name: "Math",
      tag: "Hyper Accelerated",
      districtPathwayIds: ["pathway-2"],
      coursesByGrade: [
        {
          grade: 8,
          courses: [
            {
              name: "8th Grade Math",
              code: "M0823",
            },
          ],
        },
        {
          grade: 9,
          courses: [
            {
              name: "Algebra I",
              code: "H2313",
            },
          ],
        },
        {
          grade: 10,
          courses: [
            {
              name: "Geometry",
              code: "H2413",
            },
          ],
        },
        {
          grade: 11,
          courses: [
            {
              name: "Algebra II",
              code: "H2323",
            },
          ],
        },
        {
          grade: 12,
          courses: [
            {
              name: "College-Prep Math/PreCal/Statistics",
              code: "H2715",
            },
            {
              name: "OnRamps College Algebra",
              code: "H2716",
            },
            {
              name: "AP Statistics",
              code: "H2717",
            },
          ],
        },
      ],
    },
    {
      name: "Science",
      tag: "Early Start",
      districtPathwayIds: ["pathway-2"],
      coursesByGrade: [
        {
          grade: 8,
          courses: [
            {
              name: "8th Grade Science",
              code: "M0802",
            },
          ],
        },
        {
          grade: 9,
          courses: [
            {
              name: "Honors Biology",
              code: "H3233",
            },
          ],
        },
        {
          grade: 10,
          courses: [
            {
              name: "Honors Chemistry",
              code: "H3333",
            },
          ],
        },
        {
          grade: 11,
          courses: [
            {
              name: "Physics",
              code: "H3243",
            },
          ],
        },
        {
          grade: 12,
          courses: [
            {
              name: "AP Chemistry",
              code: "H1113",
            },
            {
              name: "AP Biology",
              code: "H1114",
            },
            {
              name: "Environmental Science",
              code: "H1115",
            },
          ],
        },
      ],
    },
    {
      name: "Social Studies",
      tag: "Academics",
      districtPathwayIds: ["pathway-3"],
      coursesByGrade: [
        {
          grade: 8,
          courses: [
            {
              name: "8th Grade American History",
              code: "M0843",
            },
          ],
        },
        {
          grade: 9,
          courses: [
            {
              name: "World Geography",
              code: "H4120",
            },
          ],
        },
        {
          grade: 10,
          courses: [
            {
              name: "World History",
              code: "H4213",
            },
          ],
        },
        {
          grade: 11,
          courses: [
            {
              name: "U.S. History",
              code: "H4113",
            },
          ],
        },
        {
          grade: 12,
          courses: [
            {
              name: "Government",
              code: "H4346",
            },
            {
              name: "Economics",
              code: "H4347",
            },
            {
              name: "AP Government",
              code: "H4348",
            },
          ],
        },
      ],
    },
    {
      name: "World Languages",
      tag: "French",
      districtPathwayIds: ["pathway-3"],
      coursesByGrade: [
        {
          grade: 8,
          courses: [],
        },
        {
          grade: 9,
          courses: [
            {
              name: "French I",
              code: "H5013",
            },
          ],
        },
        {
          grade: 10,
          courses: [
            {
              name: "French II",
              code: "H5023",
            },
            {
              name: "French II Honors",
              code: "H5024",
            },
            {
              name: "French Conversation",
              code: "H5025",
            },
          ],
        },
        {
          grade: 11,
          courses: [
            {
              name: "French III H",
              code: "H5034",
            },
          ],
        },
        {
          grade: 12,
          courses: [
            {
              name: "French IV H",
              code: "H5044",
            },
            {
              name: "AP French",
              code: "H5045",
            },
            {
              name: "French Literature",
              code: "H5046",
            },
          ],
        },
      ],
    },
  ],
  electiveCourseSequences: [
    {
      category: "Fine Arts",
      name: "Fine Arts",
      districtPathwayIds: ["pathway-3"],
      coursesByGrade: [
        {
          grade: 8,
          courses: [],
        },
        {
          grade: 9,
          courses: [],
        },
        {
          grade: 10,
          courses: [],
        },
        {
          grade: 11,
          courses: [],
        },
        {
          grade: 12,
          courses: [
            {
              name: "Fine Art Dance",
              code: "",
            },
          ],
        },
      ],
    },
    {
      category: "PE/Athletics",
      name: "PE/Athletics",
      districtPathwayIds: ["pathway-4"],
      coursesByGrade: [
        {
          grade: 8,
          courses: [],
        },
        {
          grade: 9,
          courses: [],
        },
        {
          grade: 10,
          courses: [],
        },
        {
          grade: 11,
          courses: [],
        },
        {
          grade: 12,
          courses: [
            {
              name: "Football",
              code: "",
            },
          ],
        },
      ],
    },
  ],
};

export const CoursePlanPDFPreviewPage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Course Plan PDF Preview
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body1" paragraph>
          Click the button below to preview the Course Plan PDF. You can view it
          in the browser without downloading the file.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          This is useful for developing and testing the PDF layout without
          generating the actual PDF file. Edit the mock data in{" "}
          <code>course-plan-pdf-preview.tsx</code> to test different scenarios.
        </Typography>

        <Button
          variant="contained"
          onClick={() => setIsVisible(!isVisible)}
          sx={{ mt: 2 }}
        >
          {isVisible ? "Hide Preview" : "Show PDF Preview"}
        </Button>
      </Paper>

      {isVisible && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Live PDF Preview
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: "calc(100vh - 300px)",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "auto",
            }}
          >
            <PDFViewer width="100%" height="100%" showToolbar={true}>
              <CoursePlanPDF data={mockCoursePlanData} />
            </PDFViewer>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
